import React, { useMemo } from "react"
import { useQuery } from "@apollo/client/react"
import { Common } from "lib/types"
import {
  GetPaymentProvidersDocument,
  GetPaymentProvidersQuery
} from "lib/types/generated/graphql"
import {
  ComparisonString,
  ComparisonInteger,
  ComparisonType,
  MAX_COMPARISON_VALUES
} from "lib/types/workflowConditions"
import { getPaymentProviderDisplayName } from "helpers/getPaymentDisplayName"

import MultiSelect from "components/Ui/Form/MultiSelect"
import SingleSelectSimple from "components/Ui/Form/SingleSelectSimple"
import {
  AddValueButton,
  FieldRemoveIconButton,
  FormLabel,
  FormSection,
  KeyValueCard,
  KeyValueCardHeader,
  KeyValueSubsection,
  KeyValueSubsectionLabel,
  SmallActionButton,
  ValueInput
} from "./NodeEditorSidebar.styled"
import {
  EnumSource,
  FieldDescriptor
} from "./conditionSchema"
import {
  COMPARISONS,
  STRING_COMPARISONS,
  INTEGER_COMPARISONS,
  isMultiValueComparison
} from "./conditionFormHelpers"
import {
  asComparisonInteger,
  asComparisonString,
  makeEmptyKeyValuePair
} from "./checkFieldHelpers"

/** Find the matching option for a string value, or null if absent. */
const findOption = <T extends Common.Option>(
  options: readonly T[],
  value: string | undefined
): T | null => options.find((o) => o.value === value) ?? null

// ──────────────────────────────────────────────────────────────────────────
// ComparisonString — comparison + values list (free-text)
// Single-value comparators render as a text input; CONTAINS / NOT_CONTAINS
// render as a chip MultiSelect capped at MAX_COMPARISON_VALUES.
// ──────────────────────────────────────────────────────────────────────────

export const ComparisonStringEditor = ({
  value,
  onChange,
  comparisonOptions,
  placeholder
}: {
  value: ComparisonString
  onChange: (next: ComparisonString) => void
  comparisonOptions?: { value: ComparisonType; label: string }[]
  placeholder?: string
}) => {
  const opts = comparisonOptions ?? STRING_COMPARISONS
  const isMulti = isMultiValueComparison(value.comparison)
  const tagOptions: Common.Option[] = useMemo(
    () => value.values.map((v) => ({ value: v, label: v })),
    [value.values]
  )
  return (
    <>
      <FormSection>
        <FormLabel>Comparison</FormLabel>
        <SingleSelectSimple
          options={opts}
          value={findOption(opts, value.comparison)}
          setValue={(v: string) =>
            onChange({ ...value, comparison: v as ComparisonType })
          }
        />
      </FormSection>

      {isMulti ? (
        <FormSection>
          <MultiSelect
            label={`Values (max ${MAX_COMPARISON_VALUES})`}
            options={[]}
            value={tagOptions}
            setValue={(next: Common.Option[]) =>
              onChange({ ...value, values: next.map((o) => o.value) })
            }
            placeholder={placeholder ?? "Type a value and press Enter…"}
            creatable
            maxTags={MAX_COMPARISON_VALUES}
            maxTagLength={100}
          />
        </FormSection>
      ) : (
        <FormSection>
          <FormLabel>Value</FormLabel>
          <ValueInput
            type="text"
            maxLength={100}
            value={value.values[0] ?? ""}
            placeholder={placeholder}
            onChange={(e) =>
              onChange({
                ...value,
                values: e.target.value ? [e.target.value] : []
              })
            }
          />
        </FormSection>
      )}
    </>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// ComparisonInteger — comparison + integer values
// Single-value comparators render as a number input; CONTAINS / NOT_CONTAINS
// render as a chip picker.
// ──────────────────────────────────────────────────────────────────────────

export const ComparisonIntegerEditor = ({
  value,
  onChange,
  label
}: {
  value: ComparisonInteger
  onChange: (next: ComparisonInteger) => void
  label?: string
}) => {
  const isMulti = isMultiValueComparison(value.comparison)
  const tagOptions: Common.Option[] = useMemo(
    () => value.values.map((v) => ({ value: String(v), label: String(v) })),
    [value.values]
  )
  return (
    <>
      <FormSection>
        <FormLabel>{label ?? "Comparison"}</FormLabel>
        <SingleSelectSimple
          options={INTEGER_COMPARISONS}
          value={findOption(INTEGER_COMPARISONS, value.comparison)}
          setValue={(v: string) =>
            onChange({ ...value, comparison: v as ComparisonType })
          }
        />
      </FormSection>

      {isMulti ? (
        <FormSection>
          <MultiSelect
            label={`Values (max ${MAX_COMPARISON_VALUES})`}
            options={[]}
            value={tagOptions}
            setValue={(next: Common.Option[]) =>
              onChange({
                ...value,
                values: next
                  .map((o) => Number(o.value))
                  .filter((n) => Number.isFinite(n) && n >= 0)
              })
            }
            placeholder="Type a number and press Enter…"
            creatable
            maxTags={MAX_COMPARISON_VALUES}
          />
        </FormSection>
      ) : (
        <FormSection>
          <FormLabel>Value</FormLabel>
          <ValueInput
            type="number"
            min={0}
            value={Number.isFinite(value.values[0]) ? value.values[0] : ""}
            onChange={(e) => {
              const raw = e.target.value
              if (raw === "") {
                onChange({ ...value, values: [] })
                return
              }
              const parsed = Number(raw)
              if (Number.isFinite(parsed) && parsed >= 0) {
                onChange({ ...value, values: [parsed] })
              }
            }}
          />
        </FormSection>
      )}
    </>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// EnumComparisonString — enum-backed picker (currently: payment provider)
// EQUALS / NOT_EQUALS render as a single-select; CONTAINS / NOT_CONTAINS
// render as a multi-select.
// ──────────────────────────────────────────────────────────────────────────

export const EnumComparisonStringEditor = ({
  enumSource,
  value,
  onChange
}: {
  enumSource: EnumSource
  value: ComparisonString
  onChange: (next: ComparisonString) => void
}) => {
  const isPaymentProvider = enumSource === "paymentProvider"
  const { data: providersData, loading } = useQuery<GetPaymentProvidersQuery>(
    GetPaymentProvidersDocument,
    { skip: !isPaymentProvider }
  )

  if (!isPaymentProvider) {
    return (
      <ComparisonStringEditor
        value={value}
        onChange={onChange}
        comparisonOptions={STRING_COMPARISONS}
      />
    )
  }

  const options: Common.Option[] = useMemo(() => {
    const providers = providersData?.getPaymentProviders ?? []
    const seen = new Set<string>()
    const list: Common.Option[] = []
    for (const p of providers) {
      if (!seen.has(p.type)) {
        seen.add(p.type)
        list.push({
          value: p.type,
          label: getPaymentProviderDisplayName(p.type)
        })
      }
    }
    return list
  }, [providersData])

  const selected: Common.Option[] = useMemo(
    () =>
      value.values.map((v) => ({
        value: v,
        label: getPaymentProviderDisplayName(v)
      })),
    [value.values]
  )

  // Equality + list-membership only: the backend's compareString uses
  // targets[0] for BEGINS_WITH / ENDS_WITH / LESS_THAN, which doesn't make
  // sense on enum values.
  const enumComparisons = useMemo(
    () =>
      COMPARISONS.filter(
        (c) =>
          c.value === "EQUALS" ||
          c.value === "NOT_EQUALS" ||
          c.value === "CONTAINS" ||
          c.value === "NOT_CONTAINS"
      ),
    []
  )
  const isMulti = isMultiValueComparison(value.comparison)

  return (
    <>
      <FormSection>
        <FormLabel>Comparison</FormLabel>
        <SingleSelectSimple
          options={enumComparisons}
          value={findOption(enumComparisons, value.comparison)}
          setValue={(v: string) =>
            onChange({ ...value, comparison: v as ComparisonType })
          }
        />
      </FormSection>
      <FormSection>
        {isMulti ? (
          <MultiSelect
            label="Payment providers"
            options={options}
            value={selected}
            setValue={(next: Common.Option[]) =>
              onChange({ ...value, values: next.map((p) => p.value) })
            }
            placeholder="Select providers..."
            loading={loading}
          />
        ) : (
          <SingleSelectSimple
            label="Payment provider"
            options={options}
            value={selected[0] ?? null}
            setValue={(v: string) =>
              onChange({ ...value, values: v ? [v] : [] })
            }
            placeholder="Select a provider..."
          />
        )}
      </FormSection>
    </>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// arrayCount — wraps ComparisonInteger as `{ length }`
// ──────────────────────────────────────────────────────────────────────────

const ArrayCountEditor = ({
  value,
  onChange
}: {
  value: { length: ComparisonInteger }
  onChange: (next: { length: ComparisonInteger }) => void
}) => (
  <ComparisonIntegerEditor
    value={value.length}
    onChange={(length) => onChange({ length })}
    label="Length comparison"
  />
)

// ──────────────────────────────────────────────────────────────────────────
// KeyValues — list of optional key / value pairs
// (ORDER_LINE.customAttributes / tags / options)
// ──────────────────────────────────────────────────────────────────────────

const KeyValuesEditor = ({
  value,
  onChange
}: {
  value: {
    keyValues: { key?: ComparisonString; value?: ComparisonString }[]
  }
  onChange: (next: {
    keyValues: { key?: ComparisonString; value?: ComparisonString }[]
  }) => void
}) => {
  const updatePair = (
    idx: number,
    next: { key?: ComparisonString; value?: ComparisonString }
  ) => {
    const list = [...value.keyValues]
    list[idx] = next
    onChange({ keyValues: list })
  }
  const removePair = (idx: number) =>
    onChange({ keyValues: value.keyValues.filter((_, i) => i !== idx) })
  const addPair = () =>
    onChange({ keyValues: [...value.keyValues, makeEmptyKeyValuePair()] })

  return (
    <>
      {value.keyValues.map((pair, idx) => (
        <KeyValueCard key={idx}>
          <KeyValueCardHeader>
            <span>Pair {idx + 1}</span>
            {value.keyValues.length > 1 && (
              <FieldRemoveIconButton
                type="button"
                onClick={() => removePair(idx)}
                title="Remove pair"
                aria-label="Remove pair"
              >
                ×
              </FieldRemoveIconButton>
            )}
          </KeyValueCardHeader>
          <KeyValueSubsection>
            <KeyValueSubsectionLabel>Key</KeyValueSubsectionLabel>
            {pair.key ? (
              <ComparisonStringEditor
                value={pair.key}
                onChange={(next) => updatePair(idx, { ...pair, key: next })}
              />
            ) : (
              <SmallActionButton
                type="button"
                onClick={() =>
                  updatePair(idx, {
                    ...pair,
                    key: { comparison: "EQUALS", values: [] }
                  })
                }
              >
                + Set key
              </SmallActionButton>
            )}
          </KeyValueSubsection>
          <KeyValueSubsection>
            <KeyValueSubsectionLabel>Value</KeyValueSubsectionLabel>
            {pair.value ? (
              <ComparisonStringEditor
                value={pair.value}
                onChange={(next) => updatePair(idx, { ...pair, value: next })}
              />
            ) : (
              <SmallActionButton
                type="button"
                onClick={() =>
                  updatePair(idx, {
                    ...pair,
                    value: { comparison: "EQUALS", values: [] }
                  })
                }
              >
                + Set value
              </SmallActionButton>
            )}
          </KeyValueSubsection>
        </KeyValueCard>
      ))}
      <AddValueButton type="button" onClick={addPair}>
        + Add pair
      </AddValueButton>
    </>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// FieldEditor — dispatcher: picks the right primitive based on field.kind.
// ──────────────────────────────────────────────────────────────────────────

export const FieldEditor = ({
  field,
  value,
  onChange
}: {
  field: FieldDescriptor
  value: unknown
  onChange: (next: unknown) => void
}) => {
  switch (field.kind) {
    case "comparisonString":
      return (
        <ComparisonStringEditor
          value={asComparisonString(value)!}
          onChange={onChange}
          placeholder={field.placeholder}
        />
      )
    case "comparisonStringEnum":
      return (
        <EnumComparisonStringEditor
          enumSource={field.enumSource!}
          value={asComparisonString(value)!}
          onChange={onChange}
        />
      )
    case "comparisonInteger":
      return (
        <ComparisonIntegerEditor
          value={asComparisonInteger(value)!}
          onChange={onChange}
        />
      )
    case "arrayCount":
      return (
        <ArrayCountEditor
          value={value as { length: ComparisonInteger }}
          onChange={onChange}
        />
      )
    case "keyValues":
      return (
        <KeyValuesEditor
          value={
            value as {
              keyValues: { key?: ComparisonString; value?: ComparisonString }[]
            }
          }
          onChange={onChange}
        />
      )
  }
}
